import React from "react";

import { NOTE_STYLES, NOTE_FONT } from "./constants";

// The eight resize anchors, as unit offsets from the element's top-left.
export const HANDLES = [
  { key: "nw", fx: 0, fy: 0, cursor: "nwse-resize" },
  { key: "n", fx: 0.5, fy: 0, cursor: "ns-resize" },
  { key: "ne", fx: 1, fy: 0, cursor: "nesw-resize" },
  { key: "e", fx: 1, fy: 0.5, cursor: "ew-resize" },
  { key: "se", fx: 1, fy: 1, cursor: "nwse-resize" },
  { key: "s", fx: 0.5, fy: 1, cursor: "ns-resize" },
  { key: "sw", fx: 0, fy: 1, cursor: "nesw-resize" },
  { key: "w", fx: 0, fy: 0.5, cursor: "ew-resize" },
];

// Inner content for each element type. The wrapper handles position,
// rotation and selection chrome so these only describe appearance.
function ElementBody({ el, note, editing, onTextChange, onTextCommit }) {
  const stroke =
    el.stroke && el.stroke !== "transparent" && el.strokeWidth > 0
      ? `${el.strokeWidth}px solid ${el.stroke}`
      : "none";

  if (el.type === "note") {
    const style = NOTE_STYLES[note?.paletteIndex ?? el.paletteIndex] || NOTE_STYLES[0];
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          background: style.paper,
          borderRadius: "4px",
          boxShadow: "0 10px 18px rgba(15,23,42,0.28)",
          padding: "22px 12px 12px",
          overflow: "hidden",
        }}
      >
        {/* Magnet pin */}
        <span
          style={{
            position: "absolute",
            top: "-11px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "22px",
            height: "22px",
            borderRadius: "50%",
            background: `radial-gradient(circle at 33% 28%, rgba(255,255,255,0.95), ${style.pin} 72%)`,
            boxShadow:
              "0 3px 5px rgba(0,0,0,0.4), inset 0 -2px 3px rgba(0,0,0,0.25)",
          }}
        />
        {editing ? (
          // Edits the underlying sticky note, so the change lands on the
          // board and on every other canvas this note appears in.
          <textarea
            autoFocus
            value={note?.text ?? el.text}
            onChange={(e) => onTextChange(e.target.value)}
            onBlur={onTextCommit}
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              height: "100%",
              background: "transparent",
              border: "none",
              outline: "none",
              resize: "none",
              fontFamily: NOTE_FONT,
              fontSize: `${el.fontSize}px`,
              lineHeight: 1.35,
              color: "#1f2937",
              textAlign: el.align,
            }}
          />
        ) : (
          <div
            style={{
              fontFamily: NOTE_FONT,
              fontSize: `${el.fontSize}px`,
              lineHeight: 1.35,
              color: "#1f2937",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              textAlign: el.align,
            }}
          >
            {note?.text ?? el.text}
          </div>
        )}
      </div>
    );
  }

  if (el.type === "text") {
    const textStyle = {
      width: "100%",
      height: "100%",
      fontFamily: el.fontFamily,
      fontSize: `${el.fontSize}px`,
      fontWeight: el.fontWeight,
      color: el.color,
      textAlign: el.align,
      lineHeight: 1.35,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      padding: "4px",
    };

    if (editing) {
      return (
        <textarea
          autoFocus
          value={el.text}
          onChange={(e) => onTextChange(e.target.value)}
          onBlur={onTextCommit}
          // Let the caret move without the surface treating it as a drag.
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            ...textStyle,
            background: "rgba(255,255,255,0.75)",
            border: "1px solid #ff6b35",
            borderRadius: "4px",
            outline: "none",
            resize: "none",
          }}
        />
      );
    }
    return <div style={textStyle}>{el.text}</div>;
  }

  if (el.type === "rect") {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          background: el.fill,
          border: stroke,
          borderRadius: `${el.radius}px`,
        }}
      />
    );
  }

  if (el.type === "ellipse") {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          background: el.fill,
          border: stroke,
          borderRadius: "50%",
        }}
      />
    );
  }

  if (el.type === "line") {
    return (
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: 0,
          width: "100%",
          height: `${el.strokeWidth || 2}px`,
          transform: "translateY(-50%)",
          background: el.stroke === "transparent" ? el.fill : el.stroke,
          borderRadius: "999px",
        }}
      />
    );
  }

  if (el.type === "image") {
    return (
      <img
        src={el.src}
        alt=""
        draggable={false}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          border: stroke,
          borderRadius: `${el.radius}px`,
          display: "block",
        }}
      />
    );
  }

  return null;
}

function ElementView({
  el,
  note,
  selected,
  editing,
  editable,
  scale,
  onPointerDown,
  onDoubleClick,
  onTextChange,
  onTextCommit,
}) {
  // Selection chrome is drawn in canvas units but should stay a constant
  // size on screen, so counter-scale it by the current zoom.
  const px = (n) => `${n / scale}px`;

  return (
    <div
      onPointerDown={(e) => onPointerDown(e, "move")}
      onDoubleClick={onDoubleClick}
      style={{
        position: "absolute",
        left: `${el.x}px`,
        top: `${el.y}px`,
        width: `${el.w}px`,
        height: `${el.h}px`,
        transform: `rotate(${el.rotation}deg)`,
        transformOrigin: "center center",
        opacity: el.opacity,
        zIndex: el.z,
        cursor: editable ? (editing ? "text" : "move") : "default",
        touchAction: "none",
        userSelect: "none",
      }}
    >
      <ElementBody
        el={el}
        note={note}
        editing={editing}
        onTextChange={onTextChange}
        onTextCommit={onTextCommit}
      />

      {selected && editable && (
        <>
          {/* Selection outline */}
          <div
            style={{
              position: "absolute",
              inset: px(-2),
              border: `${px(2)} solid #ff6b35`,
              borderRadius: px(3),
              pointerEvents: "none",
            }}
          />

          {/* Rotate handle, on a stalk above the top edge */}
          <div
            onPointerDown={(e) => {
              e.stopPropagation();
              onPointerDown(e, "rotate");
            }}
            title="Drag to rotate"
            style={{
              position: "absolute",
              left: "50%",
              top: px(-30),
              width: px(16),
              height: px(16),
              marginLeft: px(-8),
              borderRadius: "50%",
              background: "#ff6b35",
              border: `${px(2)} solid #fff`,
              boxShadow: "0 1px 4px rgba(15,23,42,0.4)",
              cursor: "grab",
              touchAction: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: px(-16),
              width: px(2),
              height: px(16),
              marginLeft: px(-1),
              background: "#ff6b35",
              pointerEvents: "none",
            }}
          />

          {/* Resize handles — lines only get the two horizontal anchors */}
          {HANDLES.filter((h) => el.type !== "line" || h.fy === 0.5).map((h) => (
            <div
              key={h.key}
              onPointerDown={(e) => {
                e.stopPropagation();
                onPointerDown(e, "resize", h.key);
              }}
              style={{
                position: "absolute",
                left: `calc(${h.fx * 100}% - ${px(6)})`,
                top: `calc(${h.fy * 100}% - ${px(6)})`,
                width: px(12),
                height: px(12),
                borderRadius: px(3),
                background: "#fff",
                border: `${px(2)} solid #ff6b35`,
                cursor: h.cursor,
                touchAction: "none",
              }}
            />
          ))}
        </>
      )}
    </div>
  );
}

export default ElementView;
